/**
 * Conservative world-XY AABB extraction for CAD entities.
 *
 * Fast, allocation-light paths for the common drafting types (the 50k-entity
 * drafting target never tessellates); everything else falls back to the
 * resolved polyline bounds, and types without resolvable geometry return
 * null → the EntityIndex files them as "unbounded" (always returned from
 * queries, mirroring the OCS reference behavior for degenerate boxes).
 */

import type { Entity } from '../../state/store';
import type { AABB } from './quadtree';
import { aabbOfPoints } from './quadtree';
import { expandBulgePolyline, type Pt3 } from '../commands/geometryMath';
import { entityToPolyline } from '../commands/runners/lib';

type Num3 = readonly [number, number, number];

/** Entity types intentionally NOT indexed (OCS: insert sub-trees live in the unbounded bag). */
export const UNINDEXED_ENTITY_TYPES = new Set(['insert', 'viewport']);

function unionInto(target: { minX: number; minY: number; maxX: number; maxY: number }, x: number, y: number): void {
    if (x < target.minX) target.minX = x;
    if (y < target.minY) target.minY = y;
    if (x > target.maxX) target.maxX = x;
    if (y > target.maxY) target.maxY = y;
}

function finish(acc: { minX: number; minY: number; maxX: number; maxY: number }): AABB | null {
    return acc.minX > acc.maxX ? null : [acc.minX, acc.minY, acc.maxX, acc.maxY];
}

function boundsOfPoints3(points: readonly Num3[]): AABB | null {
    const acc = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    for (const p of points) {
        if (!p) return null;
        const x = p[0], y = p[1];
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
        unionInto(acc, x, y);
    }
    return finish(acc);
}

function num(v: unknown, fallback = 0): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function point3(v: unknown): Num3 | null {
    return Array.isArray(v) && v.length >= 2 && Number.isFinite(v[0]) && Number.isFinite(v[1])
        ? [v[0] as number, v[1] as number, (v[2] as number) ?? 0]
        : null;
}

/**
 * Conservative XY bounds for one entity, or null when the geometry cannot
 * be bounded (non-finite data, unknown type). Null callers become unbounded.
 */
export function entityAABB(e: Entity): AABB | null {
    const d = (e.data ?? {}) as Record<string, unknown>;
    switch (e.type) {
        case 'line': {
            const a = point3(d.start), b = point3(d.end);
            return a && b ? boundsOfPoints3([a, b]) : null;
        }
        case 'polyline':
        case 'polygon': {
            const raw = Array.isArray(d.vertices) ? (d.vertices as Pt3[]) : null;
            if (!raw || raw.length < 2) return null;
            const bulges = Array.isArray(d.bulges) && d.bulges.some((b) => typeof b === 'number' && b !== 0)
                ? (d.bulges as number[])
                : undefined;
            if (bulges) {
                // Bulged segments arc away from the chord; tessellate the
                // rare bulged subset so the box stays exact.
                return boundsOfPoints3(expandBulgePolyline(raw, bulges, !!d.closed));
            }
            return boundsOfPoints3(raw);
        }
        case 'rectangle': {
            const a = point3(d.corner1), b = point3(d.corner2);
            return a && b ? boundsOfPoints3([a, b]) : null;
        }
        case 'circle': {
            const c = point3(d.center);
            if (!c) return null;
            const r = num(d.radius);
            return [c[0] - r, c[1] - r, c[0] + r, c[1] + r];
        }
        case 'arc': {
            // Conservative full-circle rim (cheap; the exact sweep box is
            // not worth the trig per entity on the rebuild path).
            const c = point3(d.center);
            if (!c) return null;
            const r = num(d.radius);
            return [c[0] - r, c[1] - r, c[0] + r, c[1] + r];
        }
        case 'ellipse': {
            const c = point3(d.center);
            if (!c) return null;
            const ra = num(d.radiusA, 1), rb = num(d.radiusB, ra);
            const rot = num(d.rotation);
            const cos = Math.cos(rot), sin = Math.sin(rot);
            // Exact support extents of a rotated ellipse.
            const ex = Math.sqrt((ra * cos) ** 2 + (rb * sin) ** 2);
            const ey = Math.sqrt((ra * sin) ** 2 + (rb * cos) ** 2);
            return [c[0] - ex, c[1] - ey, c[0] + ex, c[1] + ey];
        }
        case 'point': {
            const p = point3(d.position);
            return p ? boundsOfPoints3([p]) : null;
        }
        case 'hatch': {
            const pts = Array.isArray(d.boundary) ? (d.boundary as unknown[]) : null;
            return pts && pts.length >= 2 ? boundsOfPoints3(pts.map((p) => point3(p)).filter((p): p is Num3 => !!p)) : null;
        }
        case 'leader': {
            const a = point3(d.tip), b = point3(d.landing);
            return a && b ? boundsOfPoints3([a, b]) : null;
        }
        case 'image': {
            const pos = point3(d.position);
            if (!pos) return null;
            const w = num(d.width, 1), h = num(d.height, 1);
            return boundsOfPoints3([pos, [pos[0] + w, pos[1] + h, pos[2]]]);
        }
        case 'box': {
            const a = point3(d.corner1), b = point3(d.corner2);
            if (a && b) {
                const h = num(d.height);
                return boundsOfPoints3([a, b, [a[0], a[1], a[2] + h], [b[0], b[1], b[2] + h]]);
            }
            const p = point3(d.position), s = point3(d.size);
            return p && s ? boundsOfPoints3([p, [p[0] + s[0], p[1] + s[1], p[2] + s[2]]]) : null;
        }
        case 'mesh':
        case 'solid': {
            const pts = Array.isArray(d.vertices) ? (d.vertices as unknown[]) : null;
            return pts && pts.length > 0 ? boundsOfPoints3(pts.map((p) => point3(p)).filter((p): p is Num3 => !!p)) : null;
        }
        case 'spline': {
            const tess = Array.isArray(d.tessellation) ? (d.tessellation as Num3[]) : null;
            const cvs = Array.isArray(d.controlPoints) ? (d.controlPoints as Num3[]) : null;
            const pts = tess && tess.length >= 2 ? tess : cvs;
            return pts && pts.length >= 2 ? boundsOfPoints3(pts) : null;
        }
        default: {
            if (UNINDEXED_ENTITY_TYPES.has(e.type)) return null;
            // Generic fallback: resolved polyline bounds (covers dimensions'
            // geometry where present, curves, 3D polylines, ...).
            const pts = entityToPolyline(e);
            if (!pts || pts.length === 0) return null;
            return aabbOfPoints(pts.map((p) => [p[0], p[1]] as const));
        }
    }
}
